import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { palette, radii, shadow } from '../../styles/tokens';

/**
 * TimePicker — admin twin.
 *
 * Storage 'HH:MM' (24h) or 'HH:MM:SS' on read (seconds trimmed). Emits 'HH:MM'.
 * PG `time` columns cast 'HH:MM' → 'HH:MM:00' implicitly on insert.
 *
 * Q6 Phase 1.2 (Session 78).
 */

export interface TimePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  step?: 15 | 30;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  autoFocus?: boolean;
  minTime?: string;
  clearable?: boolean;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function trimSeconds(s: string | null | undefined): string | null {
  if (!s) return null;
  const parts = s.split(':');
  if (parts.length < 2) return null;
  return `${pad(Number(parts[0]))}:${pad(Number(parts[1]))}`;
}

function format12(hhmm: string | null): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad(m)} ${ampm}`;
}

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

function buildMinuteOptions(step: 15 | 30): number[] {
  const out: number[] = [];
  for (let m = 0; m < 60; m += step) out.push(m);
  return out;
}

const WRAPPER: React.CSSProperties = { position: 'relative', width: '100%' };

const ANCHOR: React.CSSProperties = {
  width: '100%',
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  cursor: 'pointer',
  textAlign: 'left',
  outline: 'none',
  boxSizing: 'border-box',
};

const ANCHOR_DISABLED: React.CSSProperties = {
  background: palette.g50, color: palette.g400, cursor: 'not-allowed',
};

const ANCHOR_ERROR: React.CSSProperties = { borderColor: palette.red };

const ANCHOR_LABEL: React.CSSProperties = { flex: 1, color: palette.g800 };
const ANCHOR_PLACEHOLDER: React.CSSProperties = { flex: 1, color: palette.g400 };

const CLEAR_BTN: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 18, height: 18, borderRadius: 9,
  border: 'none', background: 'transparent',
  color: palette.g500, cursor: 'pointer', padding: 0,
};

const POPOVER: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)', left: 0,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.md,
  boxShadow: shadow.md,
  padding: 8,
  display: 'flex',
  gap: 4,
  width: 220,
  zIndex: 30,
};

const POPOVER_FLIP: React.CSSProperties = {
  ...POPOVER, top: 'auto', bottom: 'calc(100% + 4px)',
};

const COL: React.CSSProperties = {
  flex: 1, maxHeight: 220, overflowY: 'auto',
  display: 'flex', flexDirection: 'column', gap: 2,
};

const OPT: React.CSSProperties = {
  border: 'none', background: 'transparent',
  padding: '6px 8px', borderRadius: 6,
  fontSize: 13, color: palette.g800, cursor: 'pointer',
  fontFamily: 'inherit', textAlign: 'center',
};

const OPT_SELECTED: React.CSSProperties = {
  ...OPT, background: palette.accent, color: palette.white, fontWeight: 600,
};

const OPT_DISABLED: React.CSSProperties = {
  ...OPT, color: palette.g300, cursor: 'not-allowed',
};

const ERROR_TEXT: React.CSSProperties = { fontSize: 12, color: palette.red, marginTop: 4 };

export function TimePicker({
  value,
  onChange,
  step = 15,
  disabled = false,
  error,
  placeholder = 'Select time',
  autoFocus = false,
  minTime,
  clearable = true,
}: TimePickerProps) {
  const norm = trimSeconds(value);
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const minNorm = trimSeconds(minTime);
  const minutes = useMemo(() => buildMinuteOptions(step), [step]);

  const [h12, setH12] = useState<number>(() => {
    if (!norm) return 9;
    const h = parseInt(norm.split(':')[0], 10);
    return h % 12 || 12;
  });
  const [m, setM] = useState<number>(() => {
    if (!norm) return 0;
    const min = parseInt(norm.split(':')[1], 10);
    const snapped = Math.floor(min / step) * step;
    return snapped;
  });
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(() => {
    if (!norm) return 'AM';
    const h = parseInt(norm.split(':')[0], 10);
    return h >= 12 ? 'PM' : 'AM';
  });

  useEffect(() => {
    if (autoFocus) anchorRef.current?.focus();
  }, [autoFocus]);

  /* eslint-disable react-hooks/set-state-in-effect -- prop-sync: re-derive 12h decomposition when value changes externally */
  useEffect(() => {
    if (!norm) return;
    const h = parseInt(norm.split(':')[0], 10);
    const min = parseInt(norm.split(':')[1], 10);
    setH12(h % 12 || 12);
    setM(Math.floor(min / step) * step);
    setAmpm(h >= 12 ? 'PM' : 'AM');
  }, [norm, step]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- popover flip-up viewport measurement
    setFlip(spaceBelow < 280 && rect.top > 280);
  }, [open]);

  const isDisabledTime = useCallback((hh: number, mm: number, ap: 'AM' | 'PM') => {
    if (!minNorm) return false;
    const h24 = ap === 'PM' ? (hh % 12) + 12 : hh % 12;
    const candidate = `${pad(h24)}:${pad(mm)}`;
    return candidate < minNorm;
  }, [minNorm]);

  const emit = useCallback((hh: number, mm: number, ap: 'AM' | 'PM') => {
    const h24 = ap === 'PM' ? (hh % 12) + 12 : hh % 12;
    onChange(`${pad(h24)}:${pad(mm)}`);
  }, [onChange]);

  const pickHour = useCallback((hh: number) => {
    if (isDisabledTime(hh, m, ampm)) return;
    setH12(hh);
    emit(hh, m, ampm);
  }, [m, ampm, emit, isDisabledTime]);

  const pickMinute = useCallback((mm: number) => {
    if (isDisabledTime(h12, mm, ampm)) return;
    setM(mm);
    emit(h12, mm, ampm);
  }, [h12, ampm, emit, isDisabledTime]);

  const pickAmpm = useCallback((ap: 'AM' | 'PM') => {
    if (isDisabledTime(h12, m, ap)) return;
    setAmpm(ap);
    emit(h12, m, ap);
  }, [h12, m, emit, isDisabledTime]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  }, [onChange]);

  const showClear = clearable && !!value && !disabled;
  const popoverStyle = flip ? POPOVER_FLIP : POPOVER;

  return (
    <div style={WRAPPER} ref={wrapperRef}>
      <button
        ref={anchorRef}
        type="button"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="time-picker-popover"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          ...ANCHOR,
          ...(error ? ANCHOR_ERROR : null),
          ...(disabled ? ANCHOR_DISABLED : null),
        }}
      >
        <Clock size={14} color={disabled ? palette.g400 : palette.g500} />
        <span style={value ? ANCHOR_LABEL : ANCHOR_PLACEHOLDER}>
          {value ? format12(norm) : placeholder}
        </span>
        {showClear && (
          <button
            type="button"
            aria-label="Clear time"
            onClick={handleClear}
            style={CLEAR_BTN}
          >
            <X size={12} />
          </button>
        )}
      </button>

      {open && (
        <div
          id="time-picker-popover"
          role="dialog"
          aria-label="Choose time"
          style={popoverStyle}
        >
          <div style={COL} role="listbox" aria-label="Hour">
            {HOUR_OPTIONS.map((hh) => {
              const dis = isDisabledTime(hh, m, ampm);
              const sel = hh === h12;
              const style = dis ? OPT_DISABLED : sel ? OPT_SELECTED : OPT;
              return (
                <button
                  key={hh}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  data-testid={`hour-${hh}`}
                  disabled={dis}
                  onClick={() => pickHour(hh)}
                  style={style}
                >
                  {hh}
                </button>
              );
            })}
          </div>
          <div style={COL} role="listbox" aria-label="Minute">
            {minutes.map((mm) => {
              const dis = isDisabledTime(h12, mm, ampm);
              const sel = mm === m;
              const style = dis ? OPT_DISABLED : sel ? OPT_SELECTED : OPT;
              return (
                <button
                  key={mm}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  data-testid={`minute-${mm}`}
                  disabled={dis}
                  onClick={() => pickMinute(mm)}
                  style={style}
                >
                  {pad(mm)}
                </button>
              );
            })}
          </div>
          <div style={COL} role="listbox" aria-label="AM or PM">
            {(['AM', 'PM'] as const).map((ap) => {
              const dis = isDisabledTime(h12, m, ap);
              const sel = ap === ampm;
              const style = dis ? OPT_DISABLED : sel ? OPT_SELECTED : OPT;
              return (
                <button
                  key={ap}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  data-testid={`ampm-${ap}`}
                  disabled={dis}
                  onClick={() => pickAmpm(ap)}
                  style={style}
                >
                  {ap}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
